import React, { Component } from 'react';
import { BadgeCheckIcon, BookOpenCheckIcon, CheckCircle2Icon, ChevronDownIcon, CreditCardIcon, HeartHandshakeIcon, LockKeyholeIcon, MailIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScreenInit } from '../useScreenInit';
import { Countdown } from '../components/Countdown';
import { OfferCard } from '../components/OfferCard';
import { PurchaseToast } from '../components/PurchaseToast';
const standardUrl = 'https://colorpack.online/cart/47984977215627:1';
const diaryUrl = 'https://colorpack.online/cart/47984977805451:1';
const mockupUrl = 'https://cdn.magicpatterns.com/uploads/qJLLJj47GyMyqkQZtN1wAn/hablatebonito_mockup.png';
const volumes = [['01', 'Kit de Primeros Auxilios Emocionales', 'Protocolo de las 72 horas, técnicas de grounding, diario de re-cableado y tu plan de supervivencia personal.'], ['02', 'Anatomía de la Ruptura', 'Neurociencia del apego, Contacto Cero Real, Ritual de Cierre de Karito y duelo sano sin drama emocional.'], ['03', 'El Espejo de la Verdad', 'Inventario de creencias, método de cuestionamiento, auditoría de fortalezas y declaración de identidad.'], ['04', 'Límites Innegociables', 'Los 5 tipos de límites, guiones reales, patrones de complacencia y protocolo de 60 días.'], ['05', 'Tu Nueva Narrativa', 'Del háblate mal al háblate bonito: reescritura de historia, manifiesto personal y nueva voz interna.'], ['06', 'El Plan Maestro', 'Prevención de recaídas, sistema de mantenimiento emocional, plan de 90 días y Declaración de Vida 2.0.']];
const bonuses = [['01', 'SOS 2 A.M.', 'Calma tu mente de noche sin escribirle a nadie', '$37'], ['02', '21 Frases de Rescate', 'Cambia la frase cruel por una que te sostenga en 60 segundos', '$27'], ['03', 'No Le Escribas Todavía', 'Sobrevive el impulso sin traicionar tu proceso', '$27'], ['04', 'Mi Semana de Amor Propio Mínimo', '7 días de microacciones para volver a ti sin presión', '$17'], ['05', 'Detox Digital Emocional', 'Limpia tu celular para que deje de ser una máquina de recaídas', '$17'], ['06', 'Mi Carpeta de Evidencias', 'Pruebas reales de que sí valgo para los días de duda', '$27']];
const testimonials = [['LP · Buenos Aires', 'El Protocolo de Contacto Cero me salvó de recaer tres veces. Lo leí a las 2 AM y no le escribí.'], ['RM · Ciudad de México', 'El Tomo 3 me hizo llorar dos horas. Después me sentí más liviana que en años.'], ['MG · Bogotá', 'Por primera vez en meses sentí que alguien me entendía de verdad.']];
function SectionTitle({
  eyebrow,
  title,
  description
}: {
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return <div className="mx-auto max-w-3xl text-center" data-id="element-39">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#9d3659]" data-id="element-40">
        {eyebrow}
      </p>
      <h2 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl text-[#E7B4AA]" data-id="element-41">
        {title}
      </h2>
      {description && <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#735561]" data-id="element-42">
          {description}
        </p>}
    </div>;
}
export function SalesLetter() {
  useScreenInit();
  return <main className="min-h-screen w-full overflow-x-hidden bg-[#fff9f2] text-[#341420]" data-id="element-43">
      <header className="border-b border-[#5d1d37] bg-[#230c19] px-4 py-3 text-[#fff8f1]" data-id="element-44">
        <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 text-center text-xs font-bold tracking-wide" data-id="element-45">
          <HeartHandshakeIcon className="h-4 w-4 text-[#e7b4aa]" aria-hidden="true" data-id="element-46" />
          Recuperación emocional para mujeres en todo el mundo hispano
        </div>
      </header>

      <section className="bg-[#2b0e1d] px-4 pb-14 pt-12 text-[#fff8f1] sm:pb-20 sm:pt-18" data-id="element-47">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]" data-id="element-48">
          <div className="text-center lg:text-left" data-id="element-49">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#d6a25d]/50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#e7c88f]" data-id="element-50">
              <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-51" /> Por la
              Dra. Karito
            </p>
            <h1 className="mt-6 font-serif text-5xl leading-[0.94] sm:text-6xl lg:text-7xl" data-id="element-52">
              No estás rota.
              <br data-id="element-53" />
              <span className="text-[#e7b4aa]" data-id="element-54">Te faltaba un mapa.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-rose-100/85 sm:text-xl" data-id="element-55">
              El sistema de recuperación emocional para volver a ti, una página
              y una decisión a la vez.
            </p>
            <p className="mt-4 text-sm font-semibold text-[#e4c18c]" data-id="element-56">
              6 Tomos + 6 Bonos · 300+ páginas · Todo lo que necesitas para
              sanar de verdad.
            </p>
            <a href="#oferta" className="mt-8 inline-flex min-h-14 items-center justify-center rounded-lg bg-[#d6a25d] px-6 text-sm font-extrabold uppercase tracking-wide text-[#35121f] transition hover:bg-[#ecc781] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#fff9f2]" data-id="element-57">
              Quiero mi MegaPack ahora
            </a>
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="relative mx-auto max-w-md" data-id="element-58">
            <div className="absolute inset-x-8 bottom-0 h-10 bg-[#9e5263]/35 blur-xl" aria-hidden="true" data-id="element-59" />
            <img className="relative w-full" src={mockupUrl} alt="MegaPack Háblate Bonito de la Dra. Karito con los seis tomos y material de apoyo" data-id="element-60" />
          </motion.div>
        </div>
      </section>

      <section className="border-y border-[#ead9d1] bg-[#fbf0ea] px-4 py-5" data-id="element-61">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 text-center sm:grid-cols-4" data-id="element-62">
          {[[BookOpenCheckIcon, '100% PDF'], [ZapIcon, 'Acceso en 5 min'], [HeartHandshakeIcon, 'Todo el mundo hispano'], [ShieldCheckIcon, 'Garantía 7 días']].map(([Icon, label]) => {
          const Component = Icon as typeof BookOpenCheckIcon;
          return <div key={String(label)} className="flex items-center justify-center gap-2 text-xs font-extrabold text-[#603143]" data-id="element-63">
                <Component className="h-4 w-4 text-[#9d3659]" aria-hidden="true" data-id="element-64" />
                {String(label)}
              </div>;
        })}
        </div>
      </section>

      <section className="px-4 py-16 sm:py-24" data-id="element-65">
        <div className="mx-auto max-w-6xl" data-id="element-66">
          <div className="grid gap-5 md:grid-cols-3" data-id="element-67">
            {testimonials.map(([name, quote]) => <figure key={name} className="border-l-2 border-[#d6a25d] bg-[#f9eee8] p-6" data-id="element-68">
                <div className="text-[#d6a25d]" aria-label="Cinco estrellas" data-id="element-69">
                  ★★★★★
                </div>
                <blockquote className="mt-3 font-serif text-xl leading-snug text-[#4c2332]" data-id="element-70">
                  “{quote}”
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold uppercase tracking-wider text-[#916c77]" data-id="element-71">
                  {name}
                </figcaption>
              </figure>)}
          </div>
        </div>
      </section>

      <section className="bg-[#f3e1dc] px-4 py-16 sm:py-24" data-id="element-72">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center" data-id="element-73">
          <div data-id="element-74">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#9d3659]" data-id="element-75">
              Antes de seguir
            </p>
            <h2 className="mt-4 font-serif text-5xl leading-[0.96] text-[#341420]" data-id="element-76">
              ¿Cuántas noches más vas a pasar así?
            </h2>
          </div>
          <div data-id="element-77">
            <p className="text-lg leading-relaxed text-[#614250]" data-id="element-78">
              Eres una mujer con todo por delante. Pero una ruptura, la baja
              autoestima o el agotamiento emocional pueden dejarte sin piso: sin
              saber quién eres, cómo parar los pensamientos de noche o por qué
              sigues eligiendo lo que duele.
            </p>
            <p className="mt-4 font-serif text-2xl text-[#7b2948]" data-id="element-79">
              No estás rota. Nunca te dieron las herramientas.
            </p>
          </div>
        </div>
        <div className="mx-auto mt-12 grid max-w-5xl gap-3 sm:grid-cols-2" data-id="element-80">
          {['Noches sin dormir con pensamientos que no puedes parar', 'Revisando su perfil aunque sabes que te hace daño', 'Diciéndote “ya voy a superarlo” pero sintiéndote igual', 'Cediendo en todo porque decir no se siente como perder amor', 'Suficiente un día, completamente rota al siguiente', 'Buscando respuestas que no llegan. Mañana, el mismo ciclo otra vez.'].map(item => <p key={item} className="flex gap-3 rounded-lg bg-[#fff9f2] p-4 text-sm leading-relaxed text-[#5d3a48]" data-id="element-81">
              <span className="font-bold text-[#a53a58]" data-id="element-82">×</span>
              {item}
            </p>)}
        </div>
      </section>

      <section className="px-4 py-16 sm:py-24" data-id="element-83">
        <SectionTitle eyebrow="El sistema completo" title="Tu recuperación no necesita más improvisación." description="Seis tomos para acompañarte desde el primer desborde hasta la vida que quieres volver a construir." data-id="element-84" />
        <div className="mx-auto mt-12 grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-85">
          {volumes.map(([number, title, copy]) => <article key={number} className="group border border-[#e7d4cd] bg-white p-6 transition hover:border-[#bd7788]" data-id="element-86">
              <span className="font-serif text-4xl text-[#d6a25d]" data-id="element-87">
                {number}
              </span>
              <h3 className="mt-6 font-serif text-2xl leading-tight text-[#441a2a]" data-id="element-88">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#735561]" data-id="element-89">
                {copy}
              </p>
            </article>)}
        </div>
        <div className="mx-auto mt-10 max-w-4xl border-y border-[#e5cfc5] py-7" data-id="element-90">
          <p className="text-center text-sm font-extrabold uppercase tracking-[0.14em] text-[#8b3754]" data-id="element-91">
            Cada tomo incluye
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-92">
            {['Ejercicios guiados listos para completar', 'Protocolos de emergencia para crisis', 'Frases de rescate para usar en el momento', 'Checklists, trackers y espacios de escritura', 'Base en neurociencia y psicología real', 'Un tono cálido, directo y sin positividad falsa'].map(item => <span key={item} className="flex items-start gap-2 text-sm text-[#573340]" data-id="element-93">
                <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#9d3659]" aria-hidden="true" data-id="element-94" />
                {item}
              </span>)}
          </div>
        </div>
      </section>

      <section className="bg-[#3a1227] px-4 py-16 text-[#fff9f2] sm:py-24" data-id="element-95">
        <SectionTitle eyebrow="6 bonos · siempre incluidos" title="Para los días en que necesitas sostenerte ya." data-id="element-96" />
        <div className="mx-auto mt-11 max-w-5xl overflow-x-auto rounded-xl border border-rose-200/20" aria-label="Tabla de bonos incluida en el MegaPack" data-id="element-97">
          <table className="min-w-[660px] w-full text-left" data-id="element-98">
            <thead className="bg-[#551c37] text-xs uppercase tracking-[0.15em] text-[#eac7bd]" data-id="element-99">
              <tr data-id="element-100">
                <th className="px-5 py-4" data-id="element-101">#</th>
                <th className="px-5 py-4" data-id="element-102">Bono</th>
                <th className="px-5 py-4" data-id="element-103">Promesa</th>
                <th className="px-5 py-4 text-right" data-id="element-104">Valor</th>
                <th className="px-5 py-4 text-right" data-id="element-105">Hoy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rose-100/10 text-sm" data-id="element-106">
              {bonuses.map(([number, title, promise, value]) => <tr key={number} data-id="element-107">
                  <td className="px-5 py-4 font-serif text-lg text-[#e6c48c]" data-id="element-108">
                    {number}
                  </td>
                  <td className="px-5 py-4 font-bold" data-id="element-109">{title}</td>
                  <td className="px-5 py-4 text-rose-100/75" data-id="element-110">{promise}</td>
                  <td className="px-5 py-4 text-right text-rose-100/70 line-through" data-id="element-111">
                    {value}
                  </td>
                  <td className="px-5 py-4 text-right font-extrabold text-[#e6c48c]" data-id="element-112">
                    GRATIS
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <p className="mx-auto mt-6 max-w-5xl text-right text-sm font-bold text-[#e6c48c]" data-id="element-113">
          VALOR TOTAL BONOS: $152 USD → GRATIS con tu MegaPack
        </p>
      </section>

      <section id="oferta" className="scroll-mt-4 bg-[#2b0e1d] px-4 py-16 sm:py-24" data-id="element-114">
        <Countdown data-id="element-115" />
        <div className="mx-auto mt-12 grid max-w-5xl gap-5 lg:grid-cols-2" data-id="element-116">
          <OfferCard title="MegaPack Háblate Bonito" description="Todo el sistema para empezar hoy, a tu ritmo." originalPrice="$67 USD" price="$10.99" url={standardUrl} cta="Quiero mi MegaPack" data-id="element-117" />
          <OfferCard highlighted title="MegaPack + Diario Guiado" description="El sistema completo y una página exacta para acompañarte durante 90 días." originalPrice="$97 USD" price="$17.99" url={diaryUrl} cta="Quiero el MegaPack + Diario" data-id="element-118" />
        </div>
      </section>

      <section className="px-4 py-16 sm:py-24" data-id="element-119">
        <SectionTitle eyebrow="¿Qué agrega el diario?" title="No solo saber qué hacer. Saber qué hacer mañana." data-id="element-120" />
        <div className="mx-auto mt-10 max-w-5xl overflow-x-auto border border-[#e5d2ca]" aria-label="Comparación entre las ofertas" data-id="element-121">
          <table className="min-w-[650px] w-full text-left text-sm" data-id="element-122">
            <thead className="bg-[#f5e7e0] text-[#6f2c46]" data-id="element-123">
              <tr data-id="element-124">
                <th className="px-5 py-4" data-id="element-125">Situación</th>
                <th className="px-5 py-4" data-id="element-126">Sin Diario</th>
                <th className="px-5 py-4" data-id="element-127">Con Diario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eadbd4]" data-id="element-128">
              {[['Termina el tomo y no sabe por dónde empezar mañana', 'Abandona', 'Página exacta para cada día'], ['Día difícil sin saber qué hacer', 'Revisa redes o le escribe', 'Protocolo claro listo para usar'], ['Quiere constancia pero la pierde', 'Recae con culpa', 'Tracker semanal de avance real'], ['Necesita sentirse acompañada', 'Silencio y soledad', 'Voz de la Dra. Karito en cada página']].map(([situation, without, withDiary]) => <tr key={situation} data-id="element-129">
                  <td className="px-5 py-4 font-semibold text-[#4c2837]" data-id="element-130">
                    {situation}
                  </td>
                  <td className="px-5 py-4 text-[#96556a]" data-id="element-131">{without}</td>
                  <td className="px-5 py-4 font-bold text-[#6a2743]" data-id="element-132">
                    {withDiary}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-[#f3e1dc] px-4 py-16 sm:py-24" data-id="element-133">
        <SectionTitle eyebrow="Más mujeres que ya lo tienen" title="Pequeñas decisiones. Cambios muy reales." data-id="element-134" />
        <div className="mx-auto mt-11 grid max-w-6xl gap-4 md:grid-cols-2" data-id="element-135">
          {[['Carolina R. · Medellín', 'El Tomo 4 me enseñó a decir no sin que me temblara la voz.'], ['Sofía M. · Madrid', 'Compré esto a las 11 de la noche desde el piso de mi cuarto. El Tomo 1 me acompañó hasta que pude respirar.'], ['Paola A. · Santiago de Chile', 'El Ritual de Cierre fue lo que necesitaba. Llevaba dos años sin cerrar ese ciclo. Lo hice en una tarde.'], ['Isabel G. · Lima', 'El Tomo 3 me mostró que la creencia de ‘soy difícil de amar’ la inventé a los 9 años.']].map(([name, quote]) => <figure key={name} className="bg-[#fff9f2] p-6" data-id="element-136">
              <div className="text-sm text-[#d6a25d]" data-id="element-137">★★★★★</div>
              <blockquote className="mt-3 font-serif text-xl text-[#4b2534]" data-id="element-138">
                “{quote}”
              </blockquote>
              <figcaption className="mt-4 text-xs font-extrabold uppercase tracking-wider text-[#90616f]" data-id="element-139">
                {name}
              </figcaption>
            </figure>)}
        </div>
      </section>

      <section className="px-4 py-16 sm:py-24" data-id="element-140">
        <div className="mx-auto max-w-3xl border border-[#e7cbbf] bg-[#fff4ed] p-8 text-center sm:p-12" data-id="element-141">
          <ShieldCheckIcon className="mx-auto h-9 w-9 text-[#9d3659]" aria-hidden="true" data-id="element-142" />
          <h2 className="mt-4 font-serif text-4xl text-[#441a2a]" data-id="element-143">
            Garantía de 7 días. Sin preguntas.
          </h2>
          <p className="mt-4 leading-relaxed text-[#6a4c58]" data-id="element-144">
            Abrís los tomos. Los usás. Los recorrés a tu ritmo. Si en 7 días no
            es lo que esperabas: devolución del 100%. Sin formularios. Sin
            drama. Sin preguntas.
          </p>
          <p className="mt-4 text-sm font-bold text-[#8c3451]" data-id="element-145">
            Tasa de devoluciones: menos del 1%.
          </p>
        </div>
      </section>

      <section className="bg-[#f7eee8] px-4 py-16 sm:py-24" data-id="element-146">
        <SectionTitle eyebrow="Preguntas frecuentes" title="Lo que necesitas saber antes de empezar." data-id="element-147" />
        <div className="mx-auto mt-10 max-w-3xl divide-y divide-[#dfcac1] border-y border-[#dfcac1]" data-id="element-148">
          {[['¿Sirve si no tuve una ruptura?', 'Sí. Fue diseñado para ruptura, baja autoestima y agotamiento emocional. Puedes tener una, dos o las tres.'], ['¿Necesito saber psicología?', 'No. Está escrito para cualquier mujer, sin formación previa.'], ['¿Cómo recibo el material?', 'Por email en menos de 5 minutos. Descarga inmediata desde cualquier dispositivo.'], ['¿Qué lo diferencia de un libro de autoayuda?', 'Los libros explican. El MegaPack acompaña. Cada tomo tiene ejercicios, protocolos, checklists y herramientas concretas. No solo lees. Haces.'], ['¿Y si no me siento lista?', 'Ese pensamiento es la señal de que lo necesitas. Fue diseñado para empezar desde la crisis, no después de ella.']].map(([question, answer]) => <details key={question} className="group py-5" data-id="element-149">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold text-[#482333] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#8d2f50]" data-id="element-150">
                <span data-id="element-151">{question}</span>
                <ChevronDownIcon className="h-5 w-5 shrink-0 text-[#9d3659] transition group-open:rotate-180" aria-hidden="true" data-id="element-152" />
              </summary>
              <p className="pt-3 pr-8 text-sm leading-relaxed text-[#735561]" data-id="element-153">
                {answer}
              </p>
            </details>)}
        </div>
      </section>

      <section className="bg-[#310f20] px-4 py-16 text-[#fff9f2] sm:py-24" data-id="element-154">
        <SectionTitle eyebrow="La pregunta incómoda" title="¿Cuánto cuesta no tenerlo?" data-id="element-155" />
        <div className="mx-auto mt-10 max-w-4xl overflow-x-auto border border-rose-200/20" data-id="element-156">
          <table className="min-w-[560px] w-full text-left" data-id="element-157">
            <thead className="bg-[#521a35] text-xs uppercase tracking-[0.15em] text-[#ebc7bd]" data-id="element-158">
              <tr data-id="element-159">
                <th className="px-5 py-4" data-id="element-160">Sin MegaPack</th>
                <th className="px-5 py-4" data-id="element-161">Costo real</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rose-200/10 text-sm text-rose-50/90" data-id="element-162">
              {[['Noches buscando respuestas en internet', 'Semanas, meses, años de tu vida'], ['Recaídas que reinician el dolor', 'Energía y tiempo perdido'], ['Sin herramientas de emergencia', 'El mismo ciclo la próxima semana'], ['Sin límites ni nueva narrativa', 'Autoestima que nunca termina de construirse']].map(([without, cost]) => <tr key={without} data-id="element-163">
                  <td className="px-5 py-4" data-id="element-164">{without}</td>
                  <td className="px-5 py-4 font-bold text-[#e6c48c]" data-id="element-165">{cost}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-[#e9c8c5] px-4 py-16 text-center sm:py-24" data-id="element-166">
        <p className="font-serif text-3xl leading-tight text-[#4b1c30] sm:text-4xl" data-id="element-167">
          “No necesitas estar bien para empezar.
          <br data-id="element-168" />
          Solo necesitas no abandonarte esta noche.”
        </p>
        <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-[#8e3451]" data-id="element-169">
          — Dra. Karito
        </p>
        <div className="mt-10" data-id="element-170">
          <Countdown data-id="element-171" />
        </div>
        <div className="mx-auto mt-11 grid max-w-5xl gap-5 text-left lg:grid-cols-2" data-id="element-172">
          <OfferCard title="MegaPack Háblate Bonito" description="6 tomos + 6 bonos para comenzar hoy." originalPrice="$67 USD" price="$10.99" url={standardUrl} cta="Quiero mi MegaPack" data-id="element-173" />
          <OfferCard highlighted title="MegaPack + Diario Guiado" description="Tu sistema completo de 90 días." originalPrice="$97 USD" price="$17.99" url={diaryUrl} cta="Quiero el MegaPack + Diario" data-id="element-174" />
        </div>
      </section>

      <footer className="bg-[#210b18] px-4 py-12 text-center text-rose-100/75" data-id="element-175">
        <div className="mx-auto max-w-4xl" data-id="element-176">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-xs font-bold text-[#f0d8cb]" data-id="element-177">
            <span className="inline-flex gap-1.5" data-id="element-178">
              <LockKeyholeIcon className="h-4 w-4 text-[#d6a25d]" aria-hidden="true" data-id="element-179" />
              Pago seguro
            </span>
            <span className="inline-flex gap-1.5" data-id="element-180">
              <ZapIcon className="h-4 w-4 text-[#d6a25d]" aria-hidden="true" data-id="element-181" />
              Acceso en 5 min
            </span>
            <span className="inline-flex gap-1.5" data-id="element-182">
              <CreditCardIcon className="h-4 w-4 text-[#d6a25d]" aria-hidden="true" data-id="element-183" />
              Tarjeta / PayPal
            </span>
          </div>
          <p className="mt-9 font-serif text-3xl text-[#fff8f1]" data-id="element-184">
            Dra. Karito™
          </p>
          <p className="mt-2 text-sm" data-id="element-185">
            Recuperación emocional para mujeres en todo el mundo hispano.
          </p>
          <p className="mt-1 text-sm" data-id="element-186">
            Porque sanar no debería costarte años buscando sola.
          </p>
          <p className="mt-6 inline-flex items-center gap-2 text-xs" data-id="element-187">
            <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-188" />
            contacto@drakarito.com · @drakarito · www.drakarito.com
          </p>
          <p className="mt-6 text-xs" data-id="element-189">
            © Dra. Karito™ 2026 — Todos los derechos reservados.
          </p>
        </div>
      </footer>
      <PurchaseToast data-id="element-190" />
    </main>;
}